import React, { useCallback, useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar/Navbar";
import Header from "../components/Home/Header";
import About from "../components/Home/About";
import Programi from "../components/Home/Programi";
import Features from "../components/Home/Features";
import LatestNews from "../components/Home/LatestNews";
import FeaturedNews from "../components/Home/FeaturedNews";
import Footer from "../components/Footer/Footer";
import ProjectCards from "../components/Projekti/ProjectCards";
import PopupV2 from "../components/Popup/PopupV2";
import { useTranslation } from "react-i18next";
import { fetchCmsPopup } from "../api/cmsClient";
import { getCmsLocaleFromLanguage } from "../api/cmsLocale";
import { normalizePopupV2Campaign } from "../components/Popup/popupV2Utils";
import {
  dismissPopupV2Campaign,
  getPopupV2CampaignKey,
  isPopupV2Dismissed,
} from "../components/Popup/popupV2Lifecycle";

const POPUP_REFRESH_MS = 60 * 1000;

function Main() {
  const { t, i18n } = useTranslation();
  const [popupCampaign, setPopupCampaign] = useState(null);
  const [popupMode, setPopupMode] = useState("hidden");
  const activeCampaignRef = useRef(null);
  const locale = getCmsLocaleFromLanguage(i18n.resolvedLanguage || i18n.language || "me");

  useEffect(() => {
    let isMounted = true;
    let controller = null;

    const loadPopup = async () => {
      controller?.abort();
      const requestController = new AbortController();
      controller = requestController;

      try {
        const result = await fetchCmsPopup({ locale, signal: requestController.signal });
        if (!isMounted) return;

        const payload = result?.data || {};
        if (payload.enabled !== true || !payload.campaign) {
          setPopupCampaign(null);
          return;
        }

        setPopupCampaign(normalizePopupV2Campaign(payload.campaign));
      } catch (error) {
        if (!isMounted || requestController.signal.aborted) return;
        console.warn("FPEP Popup Manager V2 is unavailable; popup stays hidden.", error);
        setPopupCampaign(null);
      }
    };

    loadPopup();
    const intervalId = window.setInterval(loadPopup, POPUP_REFRESH_MS);
    const handleFocus = () => loadPopup();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") loadPopup();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      isMounted = false;
      controller?.abort();
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [locale]);

  const popupCampaignKey = popupCampaign ? getPopupV2CampaignKey(popupCampaign) : null;

  useEffect(() => {
    if (!popupCampaign || !popupCampaignKey) {
      activeCampaignRef.current = null;
      setPopupMode("hidden");
      return;
    }

    if (activeCampaignRef.current === popupCampaignKey) return;
    activeCampaignRef.current = popupCampaignKey;

    const dismissed = isPopupV2Dismissed(popupCampaign);
    if (popupCampaign.behavior.autoOpen && !dismissed) {
      setPopupMode("modal");
      return;
    }

    setPopupMode(popupCampaign.behavior.showLauncher ? "launcher" : "hidden");
  }, [popupCampaign, popupCampaignKey]);

  useEffect(() => {
    const shouldLockPage = popupMode === "modal" && Boolean(popupCampaign);
    document.body.classList.toggle("fpep-popup-open", shouldLockPage);
    return () => document.body.classList.remove("fpep-popup-open");
  }, [popupCampaign, popupMode]);

  const handlePopupClose = useCallback(() => {
    if (!popupCampaign) return;
    dismissPopupV2Campaign(popupCampaign);
    setPopupMode(popupCampaign.behavior.showLauncher ? "launcher" : "hidden");
  }, [popupCampaign]);

  const handlePopupRestore = useCallback(() => {
    if (popupCampaign) setPopupMode("modal");
  }, [popupCampaign]);

  const handlePopupCampaignHidden = useCallback(() => {
    activeCampaignRef.current = null;
    setPopupCampaign(null);
    setPopupMode("hidden");
  }, []);

  return (
    <>
      {popupCampaign && popupMode !== "hidden" && (
        <PopupV2
          campaign={popupCampaign}
          mode={popupMode}
          language={locale}
          onClose={handlePopupClose}
          onRestore={handlePopupRestore}
          onCampaignHidden={handlePopupCampaignHidden}
        />
      )}
      <Navbar />
      <Header />
      <About />
      <Programi />
      <Features />
      <FeaturedNews />
      <LatestNews />
      <ProjectCards
        layout="home"
        title={t("home.projects.title")}
        subtitle={t("home.projects.subtitle")}
      />
      <Footer />
    </>
  );
}

export default Main;
